import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useServices } from '../app/services';
import type { Session } from '../data/repository';
import { requiresMfa } from '../lib/roles';
import { SURFACE, surfaceBlock } from '../lib/surface';
import { applyTheme, readStoredTheme } from '@antreja/ui/theme';
import type { AuthUser } from './authService';
import { errorText } from '../lib/errors';

export type AuthStatus = 'loading' | 'signed_out' | 'wrong_surface' | 'needs_mfa' | 'ready' | 'error';

interface AuthState {
  status: AuthStatus;
  user: AuthUser | null;
  session: Session | null;
  error: string | null;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { auth, repo } = useServices();
  const qc = useQueryClient();
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<AuthUser | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const u = await auth.getUser();
      setUser(u);
      if (!u) {
        setSession(null);
        setStatus('signed_out');
        return;
      }
      const ctx = await repo.getSessionContext(u.id);
      setSession(ctx);
      // Themakeuze van het account toepassen (de lokale cache zorgde al voor een flitsloze start).
      if (ctx.profile.ui_theme && ctx.profile.ui_theme !== readStoredTheme()) applyTheme(ctx.profile.ui_theme);
      // Antreja-team alleen op admin.antreja.nl, clubs alleen op beheer.antreja.nl (vóór de MFA-stap).
      if (surfaceBlock(SURFACE, ctx.platformRole)) {
        setStatus('wrong_surface');
        return;
      }
      const mfaNeeded = requiresMfa(ctx.platformRole, ctx.memberships.map((m) => m.role), ctx.installerOrgIds.length > 0);
      if (mfaNeeded) {
        const aal = await auth.getAal();
        if (aal.current !== 'aal2') {
          setStatus('needs_mfa');
          return;
        }
      }
      // Tell the demo repository who is acting, for its audit log.
      const maybeDemo = repo as unknown as { setActor?: (id: string, role: string | null) => void };
      maybeDemo.setActor?.(
        ctx.profile.id,
        ctx.platformRole ?? ctx.memberships.find((m) => m.role === 'club_admin')?.role ?? ctx.memberships[0]?.role ?? null,
      );
      setError(null);
      setStatus('ready');
    } catch (e) {
      setError(errorText(e, 'Onbekende fout'));
      setStatus('error');
    }
  }, [auth, repo]);

  useEffect(() => {
    void refresh();
    return auth.onChange(() => void refresh());
  }, [auth, refresh]);

  const signOut = useCallback(async () => {
    await auth.signOut();
    qc.clear();
  }, [auth, qc]);

  const value = useMemo(() => ({ status, user, session, error, refresh, signOut }), [status, user, session, error, refresh, signOut]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(AuthContext);
  if (!v) throw new Error('AuthProvider ontbreekt');
  return v;
}
